/**
 * Serialize Cronos explorer API rows (tokentx / txlist) to CSV with every field preserved.
 * Rows may have different keys; output uses a union of all keys + `explorer_record_type`.
 */

function escapeCsvField(val: string): string {
  if (val == null || val === '') return '';
  const s = String(val);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function cellValue(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function sortedUnionKeys(rows: Array<Record<string, unknown>>, extraFirst: string[]): string[] {
  const set = new Set<string>();
  for (const k of extraFirst) set.add(k);
  for (const r of rows) {
    for (const k of Object.keys(r)) set.add(k);
  }
  const rest = Array.from(set).filter((k) => !extraFirst.includes(k));
  rest.sort((a, b) => a.localeCompare(b));
  return [...extraFirst.filter((k) => set.has(k)), ...rest];
}

/**
 * One CSV string containing token + native + internal rows, distinguished by `explorer_record_type`.
 */
export function buildCombinedCronosExplorerRawCsv(
  tokenTransfers: Record<string, unknown>[],
  nativeTransactions: Record<string, unknown>[],
  internalTransactions: Record<string, unknown>[] = []
): string {
  const tagged: Array<Record<string, unknown>> = [
    ...tokenTransfers.map((r) => ({ ...r, explorer_record_type: 'tokentx' })),
    ...nativeTransactions.map((r) => ({ ...r, explorer_record_type: 'txlist' })),
    ...internalTransactions.map((r) => ({
      ...r,
      explorer_record_type: 'txlistinternal',
    })),
  ];
  if (tagged.length === 0) {
    return ['explorer_record_type'].join(',');
  }
  const keys = sortedUnionKeys(tagged, ['explorer_record_type']);
  const lines = [keys.map((k) => escapeCsvField(k)).join(',')];
  for (const row of tagged) {
    lines.push(
      keys.map((k) => escapeCsvField(cellValue(row[k as keyof typeof row]))).join(',')
    );
  }
  return lines.join('\r\n');
}

export function buildSingleModuleExplorerCsv(
  module: 'tokentx' | 'txlist' | 'txlistinternal',
  rows: Record<string, unknown>[]
): string {
  const tagged = rows.map((r) => ({ ...r, explorer_record_type: module }));
  if (tagged.length === 0) {
    return ['explorer_record_type'].join(',');
  }
  const keys = sortedUnionKeys(tagged, ['explorer_record_type']);
  const lines = [keys.map((k) => escapeCsvField(k)).join(',')];
  for (const row of tagged) {
    lines.push(
      keys.map((k) => escapeCsvField(cellValue(row[k as keyof typeof row]))).join(',')
    );
  }
  return lines.join('\r\n');
}
